import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class FilterManager implements Plugin {
  element: HTMLElement;
  wrapper: Element | Document;
  private resetBtns: NodeListOf<HTMLElement>;
  private applyBtns: NodeListOf<HTMLElement>;
  private syncEnabled: boolean;
  private static _syncing = false;

  constructor(element: HTMLElement) {
    this.element = element;
    this.syncEnabled = element.getAttribute("data-sync") !== "false";

    const targetSelector = element.getAttribute("data-filter-target");
    if (targetSelector) {
      this.wrapper = document.querySelector(targetSelector) || document;
    } else {
      this.wrapper =
        element.closest('[data-plugin="modal-manager"]') ||
        element.closest(".com-filters-panel-b2b") ||
        document;
    }

    this.resetBtns = element.querySelectorAll('[data-action="reset"]');
    this.applyBtns = element.querySelectorAll('[data-action="apply"]');
  }

  init(): void {
    this.resetBtns.forEach((btn) => {
      btn.addEventListener("click", this.handleReset.bind(this));
    });
    this.applyBtns.forEach((btn) => {
      btn.addEventListener("click", this.handleApply.bind(this));
    });

    this.wrapper.addEventListener(
      "filterManager:requestApply",
      this.handleApply.bind(this) as EventListener,
    );

    this.wrapper.addEventListener("filterManager:setState", ((
      e: CustomEvent,
    ) => {
      this.applyState(e.detail);
    }) as EventListener);

    document.addEventListener("filterManager:_globalSync", ((
      e: CustomEvent,
    ) => {
      if (!this.syncEnabled) return;
      const sourceWrapper = e.detail._sourceWrapper;
      if (sourceWrapper === this.wrapper) return;
      if (FilterManager._syncing) return;

      FilterManager._syncing = true;
      this.applyState(e.detail);
      FilterManager._syncing = false;
    }) as EventListener);

    document.addEventListener("filterManager:_globalReset", ((
      e: CustomEvent,
    ) => {
      if (!this.syncEnabled) return;
      const sourceWrapper = e.detail._sourceWrapper;
      if (sourceWrapper === this.wrapper) return;
      if (FilterManager._syncing) return;

      FilterManager._syncing = true;
      this.resetFilters(true);
      FilterManager._syncing = false;
    }) as EventListener);

    const isInsideModal =
      this.element.closest('[data-plugin="modal-manager"]') !== null ||
      (this.wrapper instanceof Element &&
        this.wrapper.closest('[data-plugin="modal-manager"]') !== null);

    if (isInsideModal) {
      document.addEventListener("modal:closed", (e: Event) => {
        const modalEl = (e.target as HTMLElement).closest(
          '[data-plugin="modal-manager"]',
        );
        if (!modalEl || !modalEl.contains(this.element)) return;

        document.dispatchEvent(
          new CustomEvent("filterManager:requestGlobalState", {
            detail: { targetWrapper: this.wrapper },
          }),
        );
      });
    }

    document.addEventListener("filterManager:requestGlobalState", ((
      e: CustomEvent,
    ) => {
      if (
        e.detail.targetWrapper !== this.wrapper &&
        (!this.applyBtns || this.applyBtns.length === 0)
      ) {
        this.collectAndBroadcast();
      }
    }) as EventListener);

    this.initAutoSync();
  }

  private autoSyncTimer: ReturnType<typeof setTimeout> | null = null;

  private initAutoSync(): void {
    const isInsideModal =
      this.element.closest('[data-plugin="modal-manager"]') !== null ||
      (this.wrapper instanceof Element &&
        this.wrapper.closest('[data-plugin="modal-manager"]') !== null);

    if (isInsideModal) return;

    if (this.applyBtns && this.applyBtns.length > 0) return;

    const tabletQuery = window.matchMedia("(min-width: 768px)");

    const changeEvents = [
      "core-filter-checkbox:change",
      "filterCalendar:change",
      "core-filter-range:change",
      "filterAutocomplete:select",
    ];

    changeEvents.forEach((eventName) => {
      this.wrapper.addEventListener(eventName, () => {
        if (!tabletQuery.matches) return;
        if (FilterManager._syncing) return;
        this.debouncedSync();
      });
    });

    this.wrapper.addEventListener("input", (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-plugin='filterAutocomplete']")) return;
      if (!tabletQuery.matches) return;
      if (FilterManager._syncing) return;
      this.debouncedSync();
    });
  }

  private debouncedSync(): void {
    if (this.autoSyncTimer) clearTimeout(this.autoSyncTimer);
    this.autoSyncTimer = setTimeout(() => {
      this.collectAndBroadcast();
    }, 300);
  }

  private collectAndBroadcast(targetWrapper?: Element | Document): void {
    const state: any = {
      calendar: {},
      range: {},
      autocomplete: "",
      checkboxes: {},
    };

    const handleCalendarState = (e: Event) => {
      state.calendar = (e as CustomEvent).detail;
    };
    const handleRangeState = (e: Event) => {
      state.range = (e as CustomEvent).detail;
    };
    const handleAutocompleteState = (e: Event) => {
      state.autocomplete = (e as CustomEvent).detail.value;
    };
    const handleCheckboxState = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.group) {
        state.checkboxes[detail.group] = detail.checked;
      }
    };

    document.addEventListener("filterCalendar:state", handleCalendarState);
    document.addEventListener("core-filter-range:state", handleRangeState);
    document.addEventListener(
      "filterAutocomplete:state",
      handleAutocompleteState,
    );
    document.addEventListener(
      "core-filter-checkbox:state",
      handleCheckboxState,
    );

    this.wrapper
      .querySelectorAll("[data-plugin='filterCalendar']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("filterCalendar:requestState")),
      );
    this.wrapper
      .querySelectorAll("[data-plugin='core-filter-range']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("core-filter-range:requestState")),
      );
    this.wrapper
      .querySelectorAll("[data-plugin='filterAutocomplete']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("filterAutocomplete:requestState")),
      );
    this.wrapper
      .querySelectorAll("[data-plugin='core-filter-checkbox']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("core-filter-checkbox:requestState")),
      );

    setTimeout(() => {
      document.removeEventListener("filterCalendar:state", handleCalendarState);
      document.removeEventListener("core-filter-range:state", handleRangeState);
      document.removeEventListener(
        "filterAutocomplete:state",
        handleAutocompleteState,
      );
      document.removeEventListener(
        "core-filter-checkbox:state",
        handleCheckboxState,
      );

      if (targetWrapper) {
        targetWrapper.dispatchEvent(
          new CustomEvent("filterManager:setState", {
            detail: state,
          }),
        );
      } else if (this.syncEnabled) {
        document.dispatchEvent(
          new CustomEvent("filterManager:_globalSync", {
            detail: { ...state, _sourceWrapper: this.wrapper },
          }),
        );
      }
    }, 10);
  }

  private applyState(state: any): void {
    if (state.calendar) {
      this.wrapper
        .querySelectorAll("[data-plugin='filterCalendar']")
        .forEach((el) =>
          el.dispatchEvent(
            new CustomEvent("filterCalendar:setState", {
              detail: { ...state.calendar, silent: true },
            }),
          ),
        );
    }

    if (state.range) {
      this.wrapper
        .querySelectorAll("[data-plugin='core-filter-range']")
        .forEach((el) =>
          el.dispatchEvent(
            new CustomEvent("core-filter-range:setState", {
              detail: { ...state.range, silent: true },
            }),
          ),
        );
    }

    if (state.autocomplete !== undefined) {
      this.wrapper
        .querySelectorAll("[data-plugin='filterAutocomplete']")
        .forEach((el) =>
          el.dispatchEvent(
            new CustomEvent("filterAutocomplete:setState", {
              detail: { value: state.autocomplete, silent: true },
            }),
          ),
        );
    }

    if (state.checkboxes) {
      this.wrapper
        .querySelectorAll("[data-plugin='core-filter-checkbox']")
        .forEach((el) => {
          const groupName = el.getAttribute("data-group-name") || "";
          if (state.checkboxes[groupName]) {
            el.dispatchEvent(
              new CustomEvent("core-filter-checkbox:setState", {
                detail: { checked: state.checkboxes[groupName], silent: true },
              }),
            );
          }
        });
    }
  }

  private resetFilters(silent: boolean = false): void {
    const plugins = [
      "filterCalendar",
      "core-filter-range",
      "filterAutocomplete",
      "core-filter-checkbox",
    ];

    plugins.forEach((pluginName) => {
      this.wrapper
        .querySelectorAll(`[data-plugin='${pluginName}']`)
        .forEach((el) => {
          el.dispatchEvent(
            new CustomEvent(`${pluginName}:reset`, { detail: { silent } }),
          );
        });
    });
  }

  private handleReset(e: Event): void {
    e.preventDefault();
    this.resetFilters(false);

    if (!FilterManager._syncing && this.syncEnabled) {
      document.dispatchEvent(
        new CustomEvent("filterManager:_globalReset", {
          detail: { _sourceWrapper: this.wrapper },
        }),
      );
    }
  }

  private handleApply(e: Event): void {
    e.preventDefault();

    const state: any = {
      calendar: {},
      range: {},
      autocomplete: "",
      checkboxes: {},
    };

    const handleCalendarState = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      state.calendar = detail;
    };

    const handleRangeState = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      state.range = detail;
    };

    const handleAutocompleteState = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      state.autocomplete = detail.value;
    };

    const handleCheckboxState = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.group) {
        state.checkboxes[detail.group] = detail.checked;
      }
    };

    document.addEventListener("filterCalendar:state", handleCalendarState);
    document.addEventListener("core-filter-range:state", handleRangeState);
    document.addEventListener(
      "filterAutocomplete:state",
      handleAutocompleteState,
    );
    document.addEventListener(
      "core-filter-checkbox:state",
      handleCheckboxState,
    );

    this.wrapper
      .querySelectorAll("[data-plugin='filterCalendar']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("filterCalendar:requestState")),
      );
    this.wrapper
      .querySelectorAll("[data-plugin='core-filter-range']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("core-filter-range:requestState")),
      );
    this.wrapper
      .querySelectorAll("[data-plugin='filterAutocomplete']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("filterAutocomplete:requestState")),
      );
    this.wrapper
      .querySelectorAll("[data-plugin='core-filter-checkbox']")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("core-filter-checkbox:requestState")),
      );

    setTimeout(() => {
      document.removeEventListener("filterCalendar:state", handleCalendarState);
      document.removeEventListener("core-filter-range:state", handleRangeState);
      document.removeEventListener(
        "filterAutocomplete:state",
        handleAutocompleteState,
      );
      document.removeEventListener(
        "core-filter-checkbox:state",
        handleCheckboxState,
      );

      this.element.dispatchEvent(
        new CustomEvent("filterManager:apply", {
          bubbles: true,
          detail: state,
        }),
      );

      if (!FilterManager._syncing && this.syncEnabled) {
        document.dispatchEvent(
          new CustomEvent("filterManager:_globalSync", {
            detail: { ...state, _sourceWrapper: this.wrapper },
          }),
        );
      }
    }, 10);
  }

  destroy(): void {}
}

PluginRegistry.register("filterManager", FilterManager);
